import { useState, useMemo, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { 
  Plus, 
  Trash2, 
  Save,
  Eye
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { format } from "date-fns";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { AlertTriangle } from "lucide-react";
import QRCode from "qrcode";
import { API } from "@/lib/api";

const SERVICE_TYPES = [
  "Ironing",
  "Wash and Fold",
  "Wash and Iron",
  "Dry Cleaning",
  "Tailoring",
  "Other Services",
  "Commerce"
];

const initialLineItem = {
  service_type: "",
  custom_service: "",
  quantity: 1,
  cost_price: 0,
  selling_price: 0
};

export const CreateInvoice = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const orderId = searchParams.get("order_id") || "";

  const DEFAULT_PAYMENT_LINK = "https://razorpay.me/@pokus";
  const [formData, setFormData] = useState({
    customer_name: searchParams.get("customer_name") || "",
    phone: searchParams.get("phone") || "",
    contact_id: searchParams.get("contact_id") || "",
    invoice_date: new Date(),
    discount: 0,
    delivery_fee: 0,
    payment_link: "",
  });
  
  const [lineItems, setLineItems] = useState([{ ...initialLineItem }]);
  const [loading, setLoading] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  
  // Duplicate handling
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [existingInvoice, setExistingInvoice] = useState(null);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState("");

  // Razorpay auto-generation
  const [autoGenerateLink, setAutoGenerateLink] = useState(true);
  const [razorpayConfigured, setRazorpayConfigured] = useState(false);

  useEffect(() => {
    fetch(`${API}/razorpay/config`)
      .then((r) => (r.ok ? r.json() : { configured: false }))
      .then((d) => setRazorpayConfigured(Boolean(d?.configured)))
      .catch(() => setRazorpayConfigured(false));
  }, []);

  const activePaymentLink = autoGenerateLink
    ? DEFAULT_PAYMENT_LINK
    : (formData.payment_link || "").trim() || DEFAULT_PAYMENT_LINK;

  useEffect(() => {
    QRCode.toDataURL(activePaymentLink, {
      width: 120,
      margin: 1,
      color: { dark: "#0047FF", light: "#ffffff" }
    }).then(setQrCodeDataUrl).catch(console.error);
  }, [activePaymentLink]);

  // Auto-calculations
  const calculations = useMemo(() => {
    const vendor_subtotal = lineItems.reduce((sum, item) => sum + (item.quantity * item.cost_price), 0);
    const customer_subtotal = lineItems.reduce((sum, item) => sum + (item.quantity * item.selling_price), 0);
    const pokus_fees = customer_subtotal - vendor_subtotal;
    const discount = parseFloat(formData.discount) || 0;
    const delivery_fee = parseFloat(formData.delivery_fee) || 0;
    const subtotal = customer_subtotal;
    const total_payable = subtotal - discount + delivery_fee;
    
    return { subtotal, vendor_subtotal, customer_subtotal, pokus_fees, discount, delivery_fee, total_payable };
  }, [lineItems, formData.discount, formData.delivery_fee]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2
    }).format(amount);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleLineItemChange = (index, field, value) => {
    setLineItems(prev => {
      const updated = [...prev];
      if (field === "service_type") {
        updated[index] = {
          ...updated[index],
          service_type: value,
          custom_service: ""
        };
      } else if (field === "custom_service") {
        updated[index] = {
          ...updated[index],
          custom_service: value
        };
      } else {
        updated[index] = {
          ...updated[index],
          [field]: parseFloat(value) || 0
        };
      }
      return updated;
    });
  };


  const addLineItem = () => {
    setLineItems(prev => [...prev, { ...initialLineItem }]);
  };

  const removeLineItem = (index) => {
    if (lineItems.length > 1) {
      setLineItems(prev => prev.filter((_, i) => i !== index));
    }
  };

  const getDisplayServiceType = (item) => {
    if ((item.service_type === "Other Services" || item.service_type === "Commerce") && item.custom_service) {
      return item.custom_service;
    }
    return item.service_type;
  };

  const validateForm = () => {
    if (!formData.customer_name.trim()) {
      toast.error("Customer name is required");
      return false;
    }
    
    if (!formData.phone.trim()) {
      toast.error("Phone number is required");
      return false;
    }
    
    const validLineItems = lineItems.filter(item => 
      item.service_type && item.quantity > 0 && item.selling_price > 0
    );
    
    if (validLineItems.length === 0) {
      toast.error("At least one valid line item with SP is required");
      return false;
    }

    for (const item of lineItems) {
      if ((item.service_type === "Other Services" || item.service_type === "Commerce") && !item.custom_service.trim()) {
        toast.error(`Please specify the ${item.service_type === "Commerce" ? "commerce" : "service"} type`);
        return false;
      }
    }
    
    return true;
  };

  const getInvoicePayload = () => {
    const validLineItems = lineItems.filter(item => 
      item.service_type && item.quantity > 0 && item.selling_price > 0
    );

    return {
      customer_name: formData.customer_name,
      phone: formData.phone,
      contact_id: formData.contact_id,
      invoice_date: format(formData.invoice_date, "yyyy-MM-dd"),
      line_items: validLineItems.map(item => ({
        service_type: item.service_type,
        custom_service: item.custom_service || "",
        quantity: item.quantity,
        price_per_unit: item.selling_price,
      })),
      discount: formData.discount,
      delivery_fee: formData.delivery_fee,
      payment_link: autoGenerateLink
        ? DEFAULT_PAYMENT_LINK
        : (formData.payment_link || "").trim() || DEFAULT_PAYMENT_LINK,
    };
  };

  const attachRazorpayLink = async (invoice) => {
    try {
      const rawDigits = String(invoice.phone || "").replace(/\D/g, "");
      if (rawDigits.length < 10) {
        toast.warning(
          "Auto-link skipped: customer phone must be at least 10 digits"
        );
        return invoice;
      }
      const phone =
        rawDigits.length === 10 ? `+91${rawDigits}` : `+${rawDigits}`;

      const linkRes = await fetch(`${API}/razorpay/links`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount: invoice.total_payable,
          currency: "INR",
          description: `Invoice ${invoice.invoice_number} — Pokus Technologies`,
          customer_name: invoice.customer_name,
          customer_phone: phone,
          reference_id: invoice.invoice_number,
          invoice_id: invoice.id,
        }),
      });
      if (!linkRes.ok) {
        const err = await linkRes.json().catch(() => ({}));
        throw new Error(err.detail || "Razorpay link creation failed");
      }
      const link = await linkRes.json();
      if (!link?.short_url) {
        throw new Error("Razorpay returned no short URL");
      }

      const patchRes = await fetch(
        `${API}/invoices/${invoice.id}/payment-link`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ payment_link: link.short_url }),
        }
      );
      if (!patchRes.ok) {
        throw new Error("Failed to attach Razorpay link to invoice");
      }
      const updated = await patchRes.json();
      toast.success("Razorpay link generated & attached");
      return updated;
    } catch (err) {
      console.error("Auto-generate Razorpay link failed:", err);
      toast.error(
        err.message
          ? `Razorpay: ${err.message}`
          : "Could not auto-generate Razorpay link"
      );
      return invoice;
    }
  };

  const checkForDuplicate = async () => {
    const response = await fetch(`${API}/invoices/check-duplicate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        phone: formData.phone,
        invoice_date: format(formData.invoice_date, "yyyy-MM-dd"),
        total_amount: calculations.total_payable
      })
    });
    
    if (!response.ok) {
      throw new Error("Failed to check for duplicates");
    }
    
    return await response.json();
  };

  const createInvoice = async () => {
    const response = await fetch(`${API}/invoices`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(getInvoicePayload())
    });
    
    if (!response.ok) {
      throw new Error("Failed to create invoice");
    }
    
    return await response.json();
  };

  const replaceInvoice = async (existingInvoiceId) => {
    const payload = getInvoicePayload();
    payload.existing_invoice_id = existingInvoiceId;
    
    const response = await fetch(`${API}/invoices/replace`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    });
    
    if (!response.ok) {
      throw new Error("Failed to replace invoice");
    }
    
    return await response.json();
  };

  const copyPublicInvoiceLink = async (invoice) => {
    if (!invoice?.id) return;
    const url = `${window.location.origin}/pay/${invoice.id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Public invoice link copied to clipboard");
    } catch (err) {
      console.warn("Clipboard copy failed:", err);
      toast.info(`Public link: ${url}`);
    }
  };

  const generateAndDownloadPDF = async (invoice) => {
    const pdfPaymentLink = invoice.payment_link || DEFAULT_PAYMENT_LINK;
    const qrCodeImage = await QRCode.toDataURL(pdfPaymentLink, {
      width: 100,
      margin: 1,
      color: { dark: "#0047FF", light: "#ffffff" }
    });

    const previewHTML = `
      <div style="font-family: 'Inter', sans-serif; padding: 40px; max-width: 600px; background: white;">
        <div style="text-align: center; margin-bottom: 30px; padding-bottom: 20px; border-bottom: 2px solid #e2e8f0;">
          <h1 style="font-size: 24px; font-weight: bold; color: #0f172a; margin: 0;">Pokus Technologies Private Limited</h1>
          <p style="color: #64748b; margin-top: 6px; font-size: 12px;">GSTIN: 29AAQCP4908B1Z8</p>
          <p style="color: #64748b; margin-top: 8px; letter-spacing: 2px;">INVOICE</p>
        </div>
        
        <div style="display: flex; justify-content: space-between; margin-bottom: 30px;">
          <div>
            <p style="font-size: 11px; color: #64748b; text-transform: uppercase; margin: 0;">Bill To</p>
            <p style="font-size: 16px; font-weight: 600; color: #0f172a; margin: 4px 0;">${invoice.customer_name}</p>
            <p style="color: #475569; margin: 0;">${invoice.phone}</p>
          </div>
          <div style="text-align: right;">
            <p style="font-size: 11px; color: #64748b; text-transform: uppercase; margin: 0;">Invoice Number</p>
            <p style="font-size: 16px; font-weight: 600; font-family: monospace; color: #0f172a; margin: 4px 0;">${invoice.invoice_number}</p>
            <p style="font-size: 11px; color: #64748b; text-transform: uppercase; margin-top: 12px;">Date</p>
            <p style="color: #475569; margin: 4px 0;">${format(new Date(invoice.invoice_date), "PPP")}</p>
          </div>
        </div>
        
        <table style="width: 100%; border-collapse: collapse; margin-bottom: 20px;">
          <thead>
            <tr style="border-bottom: 2px solid #e2e8f0;">
              <th style="text-align: left; padding: 10px 0; font-size: 11px; color: #64748b; text-transform: uppercase;">Service</th>
              <th style="text-align: center; padding: 10px 0; font-size: 11px; color: #64748b; text-transform: uppercase;">Qty</th>
              <th style="text-align: right; padding: 10px 0; font-size: 11px; color: #64748b; text-transform: uppercase;">Price</th>
              <th style="text-align: right; padding: 10px 0; font-size: 11px; color: #64748b; text-transform: uppercase;">Total (Incl. GST)</th>
            </tr>
          </thead>
          <tbody>
            ${invoice.line_items.map(item => `
              <tr style="border-bottom: 1px solid #f1f5f9;">
                <td style="padding: 12px 0; color: #0f172a;">${item.custom_service || item.service_type}</td>
                <td style="padding: 12px 0; text-align: center; font-family: monospace; color: #475569;">${item.quantity}</td>
                <td style="padding: 12px 0; text-align: right; font-family: monospace; color: #475569;">₹${(item.price_per_unit || 0).toFixed(2)}</td>
                <td style="padding: 12px 0; text-align: right; font-family: monospace; font-weight: 500; color: #0f172a;">₹${(item.line_total || 0).toFixed(2)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        
        <div style="display: flex; justify-content: flex-end; margin-bottom: 30px;">
          <div style="width: 250px;">
            <div style="display: flex; justify-content: space-between; padding: 6px 0; font-size: 14px;">
              <span style="color: #64748b;">Subtotal (Incl. GST)</span>
              <span style="font-family: monospace; color: #0f172a;">₹${invoice.subtotal.toFixed(2)}</span>
            </div>
            ${invoice.discount > 0 ? `
              <div style="display: flex; justify-content: space-between; padding: 6px 0; font-size: 14px;">
                <span style="color: #64748b;">Discount</span>
                <span style="font-family: monospace; color: #ef4444;">-₹${invoice.discount.toFixed(2)}</span>
              </div>
            ` : ''}
            ${invoice.delivery_fee > 0 ? `
              <div style="display: flex; justify-content: space-between; padding: 6px 0; font-size: 14px;">
                <span style="color: #64748b;">Delivery Fee</span>
                <span style="font-family: monospace; color: #0f172a;">+₹${invoice.delivery_fee.toFixed(2)}</span>
              </div>
            ` : ''}
            <div style="display: flex; justify-content: space-between; padding: 12px 0; font-size: 18px; font-weight: bold; border-top: 2px solid #e2e8f0; margin-top: 8px;">
              <span style="color: #0f172a;">Total Payable</span>
              <span style="font-family: monospace; color: #0047FF;">₹${invoice.total_payable.toFixed(2)}</span>
            </div>
          </div>
        </div>
        
        <div style="text-align: center; padding-top: 20px; border-top: 1px solid #e2e8f0;">
          <p style="font-size: 14px; font-weight: 600; color: #0f172a; margin: 0 0 12px 0;">Scan to Pay</p>
          <img src="${qrCodeImage}" alt="Payment QR Code" style="width: 100px; height: 100px; margin: 0 auto 12px auto; display: block;" />
          <p style="color: #0047FF; font-size: 12px; margin: 0;">${invoice.payment_link}</p>
        </div>
        
        <div style="text-align: center; margin-top: 30px; padding-top: 20px; border-top: 1px solid #e2e8f0;">
          <p style="color: #94a3b8; font-size: 13px; margin: 0;">Thank you for your business!</p>
          <p style="color: #94a3b8; font-size: 13px; margin: 4px 0;">Pokus Technologies Private Limited</p>
        </div>
      </div>
    `;
    
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = previewHTML;
    tempDiv.style.position = 'absolute';
    tempDiv.style.left = '-9999px';
    tempDiv.style.top = '0';
    document.body.appendChild(tempDiv);
    
    try {
      const canvas = await html2canvas(tempDiv.firstChild, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff"
      });
      
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4"
      });
      
      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(`invoice_${invoice.invoice_number}.pdf`);
      
      toast.success("PDF downloaded!");
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Failed to generate PDF");
    } finally {
      document.body.removeChild(tempDiv);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    
    try {
      // Check for duplicate first
      const duplicateCheck = await checkForDuplicate();
      
      if (duplicateCheck.is_duplicate) {
        setExistingInvoice(duplicateCheck.existing_invoice);
        setDuplicateDialogOpen(true);
        setLoading(false);
        return;
      }
      
      // No duplicate, create new invoice
      let invoice = await createInvoice();
      toast.success(`Invoice ${invoice.invoice_number} created!`);

      if (autoGenerateLink && razorpayConfigured) {
        invoice = await attachRazorpayLink(invoice);
      }

      await copyPublicInvoiceLink(invoice);

      // If launched from an order, link the invoice back to the order
      if (orderId) {
        await fetch(`${API}/orders/${orderId}/link-invoice`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ customer_invoice_id: invoice.id }),
        });
        // Navigate back to the order — PDF available via "View / Download PDF" link on the order page
        navigate(`/orders/${orderId}`);
      } else {
        // Download PDF then navigate to invoice preview
        await generateAndDownloadPDF(invoice);
        navigate(`/invoice/${invoice.id}`);
      }
    } catch (error) {
      console.error("Error creating invoice:", error);
      toast.error("Failed to create invoice. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleReplace = async () => {
    setDuplicateDialogOpen(false);
    setLoading(true);
    
    try {
      let invoice = await replaceInvoice(existingInvoice.id);
      setExistingInvoice(null);
      toast.success(`Invoice replaced with ${invoice.invoice_number}!`);

      if (autoGenerateLink && razorpayConfigured) {
        invoice = await attachRazorpayLink(invoice);
      }

      await copyPublicInvoiceLink(invoice);

      if (orderId) {
        await fetch(`${API}/orders/${orderId}/link-invoice`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ customer_invoice_id: invoice.id }),
        });
        navigate(`/orders/${orderId}`);
      } else {
        await generateAndDownloadPDF(invoice);
        navigate(`/invoice/${invoice.id}`);
      }
    } catch (error) {
      console.error("Error replacing invoice:", error);
      toast.error("Failed to replace invoice");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="create-invoice-page">
      {/* Duplicate Invoice Dialog */}
      <AlertDialog open={duplicateDialogOpen} onOpenChange={setDuplicateDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Duplicate Invoice Found
            </AlertDialogTitle>
            <AlertDialogDescription>
              An invoice already exists for this customer on {format(formData.invoice_date, "PPP")}.
              {existingInvoice && (
                <div className="mt-3 p-3 bg-slate-50 rounded-lg text-sm">
                  <p><strong>Invoice:</strong> {existingInvoice.invoice_number}</p>
                  <p><strong>Amount:</strong> {formatCurrency(existingInvoice.total_payable)}</p>
                </div>
              )}
              <p className="mt-3">Do you want to replace it with the new invoice?</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleReplace}
              className="bg-amber-500 hover:bg-amber-600"
            >
              Replace Invoice
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Create Invoice</h1>
          <p className="text-slate-500 mt-1">
            {orderId ? "Invoice will be linked to this order and a signing link will be generated for the merchant." : "Fill in the details to generate a new invoice"}
          </p>
        </div>
        {orderId && (
          <button onClick={() => navigate(`/orders/${orderId}`)} className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1">
            ← Back to order
          </button>
        )}
      </div>

      <form onSubmit={handleSave}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left: Form */}
          <div className="space-y-6">
            {/* Customer Details */}
            <Card className="border-slate-200">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg">Customer Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="customer_name" className="text-sm font-medium text-slate-700">
                      Customer Name *
                    </Label>
                    <Input
                      id="customer_name"
                      value={formData.customer_name}
                      onChange={(e) => handleInputChange("customer_name", e.target.value)}
                      placeholder="Enter customer name"
                      className="input-focus-ring"
                      data-testid="input-customer-name"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-sm font-medium text-slate-700">
                      Phone Number *
                    </Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => handleInputChange("phone", e.target.value)}
                      placeholder="Enter phone number"
                      className="input-focus-ring"
                      data-testid="input-phone"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="contact_id" className="text-sm font-medium text-slate-700">
                      Contact ID
                    </Label>
                    <Input
                      id="contact_id"
                      value={formData.contact_id}
                      onChange={(e) => handleInputChange("contact_id", e.target.value)}
                      placeholder="Chatwoot Contact ID"
                      className="input-focus-ring"
                      data-testid="input-contact-id"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700">
                      Invoice Date *
                    </Label>
                    <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full justify-start text-left font-normal",
                            !formData.invoice_date && "text-muted-foreground"
                          )}
                          data-testid="input-invoice-date"
                        >
                          {formData.invoice_date ? (
                            format(formData.invoice_date, "PPP")
                          ) : (
                            "Pick a date"
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={formData.invoice_date}
                          onSelect={(date) => {
                            handleInputChange("invoice_date", date || new Date());
                            setDatePickerOpen(false);
                          }}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Line Items */}
            <Card className="border-slate-200">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Line Items</CardTitle>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addLineItem}
                    className="btn-interactive"
                    data-testid="add-line-item-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Item
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Header */}
                <div className="grid grid-cols-12 gap-3 text-xs font-medium text-slate-500 uppercase tracking-wide px-1">
                  <div className="col-span-2">Service Type</div>
                  <div className="col-span-2">Qty</div>
                  <div className="col-span-2">CP (₹)</div>
                  <div className="col-span-2">SP (₹)</div>
                  <div className="col-span-2">Pokus Fee</div>
                  <div className="col-span-1">Total</div>
                  <div className="col-span-1"></div>
                </div>
                
                {/* Items */}
                {lineItems.map((item, index) => {
                  const vendorAmt = item.quantity * item.cost_price;
                  const customerAmt = item.quantity * item.selling_price;
                  const pokusFee = customerAmt - vendorAmt;
                  const needsCustomService = item.service_type === "Other Services" || item.service_type === "Commerce";
                  
                  return (
                    <div key={index} className="space-y-2">
                      <div 
                        className="grid grid-cols-12 gap-3 items-center line-item-enter"
                        data-testid={`line-item-${index}`}
                      >
                        <div className="col-span-2">
                          <Select
                            value={item.service_type}
                            onValueChange={(value) => handleLineItemChange(index, "service_type", value)}
                          >
                            <SelectTrigger 
                              className="input-focus-ring"
                              data-testid={`input-service-type-${index}`}
                            >
                              <SelectValue placeholder="Select service" />
                            </SelectTrigger>
                            <SelectContent>
                              {SERVICE_TYPES.map((type) => (
                                <SelectItem key={type} value={type}>
                                  {type}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-2">
                          <Input
                            type="number"
                            min="0"
                            step="any"
                            value={item.quantity}
                            onChange={(e) => handleLineItemChange(index, "quantity", e.target.value)}
                            onFocus={(e) => e.target.select()}
                            className="input-focus-ring font-mono text-center"
                            data-testid={`input-quantity-${index}`}
                          />
                        </div>
                        <div className="col-span-2">
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.cost_price}
                            onChange={(e) => handleLineItemChange(index, "cost_price", e.target.value)}
                            className="input-focus-ring font-mono"
                            data-testid={`input-cp-${index}`}
                          />
                        </div>
                        <div className="col-span-2">
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.selling_price}
                            onChange={(e) => handleLineItemChange(index, "selling_price", e.target.value)}
                            className="input-focus-ring font-mono"
                            data-testid={`input-sp-${index}`}
                          />
                        </div>
                        <div className={`col-span-2 font-mono text-sm ${pokusFee >= 0 ? "text-emerald-600" : "text-red-500"}`}>
                          {formatCurrency(pokusFee)}
                        </div>
                        <div className="col-span-1 font-mono text-sm text-slate-700">
                          {formatCurrency(customerAmt)}
                        </div>
                        <div className="col-span-1 flex justify-end">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => removeLineItem(index)}
                            disabled={lineItems.length === 1}
                            className="text-slate-400 hover:text-red-500"
                            data-testid={`remove-line-item-${index}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      
                      {/* Custom service input for Other Services / Commerce */}
                      {needsCustomService && (
                        <div className="ml-0 pl-0">
                          <Input
                            value={item.custom_service}
                            onChange={(e) => handleLineItemChange(index, "custom_service", e.target.value)}
                            placeholder={item.service_type === "Commerce" ? "Specify commerce type" : "Specify service type"}
                            className="input-focus-ring"
                            data-testid={`input-custom-service-${index}`}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            {/* Additional Details */}
            <Card className="border-slate-200">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg">Additional Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="discount" className="text-sm font-medium text-slate-700">
                      Discount (₹)
                    </Label>
                    <Input
                      id="discount"
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.discount}
                      onChange={(e) => handleInputChange("discount", parseFloat(e.target.value) || 0)}
                      placeholder="0.00"
                      className="input-focus-ring font-mono"
                      data-testid="input-discount"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="delivery_fee" className="text-sm font-medium text-slate-700">
                      Delivery Fee (₹)
                    </Label>
                    <Input
                      id="delivery_fee"
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.delivery_fee}
                      onChange={(e) => handleInputChange("delivery_fee", parseFloat(e.target.value) || 0)}
                      placeholder="0.00"
                      className="input-focus-ring font-mono"
                      data-testid="input-delivery-fee"
                    />
                  </div>
                </div>

                <div className="pt-4 mt-4 border-t border-slate-100 space-y-3">
                  <div className="flex items-start justify-between gap-4 rounded-lg border border-slate-200 bg-slate-50 p-3">
                    <div className="flex-1">
                      <Label
                        htmlFor="auto_generate_link"
                        className="text-sm font-medium text-slate-700 cursor-pointer"
                      >
                        Auto-generate Razorpay link
                      </Label>
                      <p className="text-xs text-slate-500 mt-1">
                        {razorpayConfigured
                          ? "Creates a unique Razorpay Payment Link with the invoice amount & customer info, and attaches it to this invoice."
                          : "Configure RAZORPAY_KEY_ID / RAZORPAY_KEY_SECRET on the backend to enable this."}
                      </p>
                    </div>
                    <Switch
                      id="auto_generate_link"
                      checked={autoGenerateLink}
                      onCheckedChange={setAutoGenerateLink}
                      disabled={!razorpayConfigured}
                      data-testid="toggle-auto-razorpay"
                    />
                  </div>

                  {!autoGenerateLink && (
                    <>
                      <div className="flex items-center justify-between">
                        <Label
                          htmlFor="payment_link"
                          className="text-sm font-medium text-slate-700"
                        >
                          Payment Link{" "}
                          <span className="text-xs text-slate-400 font-normal">
                            (optional — paste a Razorpay link)
                          </span>
                        </Label>
                        {formData.payment_link && (
                          <button
                            type="button"
                            onClick={() => handleInputChange("payment_link", "")}
                            className="text-xs text-slate-500 hover:text-slate-700 hover:underline"
                          >
                            Reset to default
                          </button>
                        )}
                      </div>
                      <Input
                        id="payment_link"
                        type="url"
                        value={formData.payment_link}
                        onChange={(e) =>
                          handleInputChange("payment_link", e.target.value)
                        }
                        placeholder={DEFAULT_PAYMENT_LINK}
                        className="input-focus-ring w-full"
                        data-testid="input-payment-link"
                      />
                      <p className="text-xs text-slate-500">
                        Customers see this on the invoice preview, PDF and QR code.
                        Leave blank to use the default{" "}
                        <code className="px-1 py-0.5 rounded bg-slate-100 text-slate-600">
                          razorpay.me/@pokus
                        </code>
                        .
                      </p>
                    </>
                  )}

                  {autoGenerateLink && (
                    <div className="text-xs text-slate-600 bg-blue-50 border border-blue-100 rounded-md px-3 py-2">
                      A Razorpay Payment Link will be generated with amount{" "}
                      <strong>
                        ₹{(calculations.total_payable || 0).toFixed(2)}
                      </strong>{" "}
                      and customer <strong>{formData.customer_name || "—"}</strong>{" "}
                      once you save the invoice. The QR code & preview will update
                      automatically after generation.
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#0047FF] hover:bg-[#0033CC] btn-interactive h-12 text-base"
              data-testid="submit-invoice-btn"
            >
              {loading ? (
                "Creating Invoice..."
              ) : (
                <>
                  <Save className="w-5 h-5 mr-2" />
                  Save & Download PDF
                </>
              )}
            </Button>
          </div>

          {/* Right: Live Preview */}
          <div className="sticky-preview">
            <Card className="border-slate-200 invoice-preview">
              <CardHeader className="pb-4 border-b border-slate-100">
                <div className="flex items-center gap-2 text-slate-500">
                  <Eye className="w-4 h-4" />
                  <span className="text-sm font-medium">Live Preview</span>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                {/* Company Header */}
                <div className="text-center mb-8 pb-6 border-b border-slate-200">
                  <h2 className="text-xl font-bold text-slate-900">Pokus Technologies Private Limited</h2>
                  <p className="text-slate-500 text-xs mt-1">GSTIN: 29AAQCP4908B1Z8</p>
                  <p className="text-slate-500 text-sm mt-1">INVOICE</p>
                </div>

                {/* Invoice Details */}
                <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
                  <div>
                    <p className="text-slate-500">Customer</p>
                    <p className="font-medium text-slate-900">
                      {formData.customer_name || "—"}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-slate-500">Date</p>
                    <p className="font-medium text-slate-900">
                      {format(formData.invoice_date, "PPP")}
                    </p>
                  </div>
                </div>

                {/* Line Items Table */}
                <div className="mb-6">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-2 text-slate-500 font-medium">Service</th>
                        <th className="text-center py-2 text-slate-500 font-medium">Qty</th>
                        <th className="text-right py-2 text-slate-500 font-medium">CP</th>
                        <th className="text-right py-2 text-slate-500 font-medium">SP</th>
                        <th className="text-right py-2 text-slate-500 font-medium">Fee</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lineItems.map((item, index) => {
                        const vAmt = item.quantity * item.cost_price;
                        const cAmt = item.quantity * item.selling_price;
                        const fee = cAmt - vAmt;
                        return (
                          <tr key={index} className="border-b border-slate-100">
                            <td className="py-2 text-slate-900">
                              {getDisplayServiceType(item) || "—"}
                            </td>
                            <td className="py-2 text-center font-mono text-slate-700">
                              {item.quantity}
                            </td>
                            <td className="py-2 text-right font-mono text-slate-700">
                              {formatCurrency(item.cost_price)}
                            </td>
                            <td className="py-2 text-right font-mono text-slate-700">
                              {formatCurrency(item.selling_price)}
                            </td>
                            <td className={`py-2 text-right font-mono ${fee >= 0 ? "text-emerald-600" : "text-red-500"}`}>
                              {formatCurrency(fee)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Totals */}
                <div className="space-y-2 pt-4 border-t border-slate-200">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Customer Subtotal (SP)</span>
                    <span className="font-mono text-slate-900">{formatCurrency(calculations.customer_subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Vendor Subtotal (CP)</span>
                    <span className="font-mono text-slate-700">{formatCurrency(calculations.vendor_subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-emerald-600">Pokus Fees</span>
                    <span className="font-mono text-emerald-600">{formatCurrency(calculations.pokus_fees)}</span>
                  </div>
                  {calculations.discount > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Discount</span>
                      <span className="font-mono text-red-500">-{formatCurrency(calculations.discount)}</span>
                    </div>
                  )}
                  {calculations.delivery_fee > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Delivery Fee</span>
                      <span className="font-mono text-slate-900">+{formatCurrency(calculations.delivery_fee)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-lg font-bold pt-2 border-t border-slate-200">
                    <span className="text-slate-900">Customer Total</span>
                    <span className="font-mono text-[#0047FF]">{formatCurrency(calculations.total_payable)}</span>
                  </div>
                </div>

                {/* Payment Link with QR Code */}
                <div className="mt-6 pt-4 border-t border-slate-200 text-center">
                  <p className="text-sm font-semibold text-slate-700 mb-3">Scan to Pay</p>
                  {qrCodeDataUrl && (
                    <img 
                      src={qrCodeDataUrl} 
                      alt="Payment QR Code" 
                      className="w-24 h-24 mx-auto mb-2"
                    />
                  )}
                  <a
                    href={activePaymentLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#0047FF] hover:underline text-xs font-medium break-all"
                  >
                    {activePaymentLink}
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </form>
    </div>
  );
};
